import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Share2 } from 'lucide-react';
import { MetaTags } from '../../../../components/SEO/MetaTags';
import { RelatedBlogPosts } from '../../../../components/Blog/RelatedBlogPosts';
export function BlogPostPassiveAggressiveTextsKO() {
  const articleData = {
    title: '수동적 공격적인 텍스트에 대한 궁극적인 가이드',
    description: '당신을 좌절시키거나 짜증나게 하는 텍스트에 혼란스러우신가요? Toxic Detector의 AI 기반 감지로 수동적 공격적인 메시지를 식별하는 방법을 알아보세요.',
    slug: 'ultimate-guide-passive-aggressive-texts',
    publishDate: '2025-05-22',
    author: 'Toxic Detector 편집팀'
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [{
      '@type': 'Question',
      name: '수동적 공격적인 텍스트란 무엇입니까?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '적대감, 비판 또는 좌절감을 간접적으로 전달하여 종종 수신자를 혼란스럽게 하거나 짜증나게 하는 텍스트입니다.'
      }
    }, {
      '@type': 'Question',
      name: '사람들은 왜 수동적 공격적인 메시지를 보냅니까?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '공개적인 대립 없이 분노를 표현하거나, 죄책감을 유발하거나, 조작하는 은밀한 방법인 경우가 많습니다.'
      }
    }, {
      '@type': 'Question',
      name: 'AI가 메시지에서 수동적 공격적인 행동을 감지할 수 있습니까?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '네. Toxic Detector는 어조, 문구, 이모티콘 사용, 응답 패턴 및 미묘한 비꼬는 말을 분석하여 조작적인 텍스트를 표시합니다.'
      }
    }]
  };
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: articleData.title,
    description: articleData.description,
    image: 'https://toxicdetector.com/images/logo.png',
    author: {
      '@type': 'Person',
      name: articleData.author
    },
    datePublished: articleData.publishDate,
    url: `https://toxicdetector.com/ko/blog/${articleData.slug}`
  };
  const handleShare = async () => {
    try {
      await navigator.share({
        title: articleData.title,
        text: articleData.description,
        url: 'https://toxicdetector.com/'
      });
    } catch (err) {
      navigator.clipboard.writeText('https://toxicdetector.com/');
      alert('링크가 클립보드에 복사되었습니다!');
    }
  };
  return <>
      <MetaTags title="수동적 공격적인 텍스트에 대한 궁극적인 가이드 — AI가 조작을 감지하는 방법 | ToxicDetector" description="당신을 좌절시키는 텍스트에 혼란스러우신가요? AI로 수동적 공격적인 메시지를 식별하는 방법을 알아보세요." keywords="수동적 공격적인 텍스트, 은밀한 수동적 공격적 행동, 텍스트 조작 패턴, AI 수동적 공격성 탐지기" canonical={`https://toxicdetector.com/ko/blog/${articleData.slug}`} ogType="article" ogImage="https://cdn.magicpatterns.com/uploads/rgRp5kcuKrQdAWFQxfbcKt/file_0000000085487243beb3c848ebc5ef15.png" schema={[faqSchema, articleSchema]} data-id="element-8495" />
      <div className="flex flex-col w-full min-h-screen bg-[#fafaf9]" data-id="element-8496">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50 backdrop-blur-sm bg-white/95" data-id="element-8497">
          <div className="max-w-4xl mx-auto px-6 py-5" data-id="element-8498">
            <div className="flex items-center justify-between" data-id="element-8499">
              <Link to="/" className="text-2xl font-bold tracking-tight" data-id="element-8500">
                🚩 ToxicDetector
              </Link>
              <button onClick={handleShare} className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors" aria-label="기사 공유" data-id="element-8501">
                <Share2 size={20} data-id="element-8502" />
              </button>
            </div>
          </div>
        </header>

        <article className="max-w-4xl mx-auto w-full px-6 py-12" data-id="element-8503">
          <Link to="/" className="inline-flex items-center text-gray-600 text-sm mb-10 hover:text-gray-900 transition-colors" data-id="element-8504">
            <ArrowLeft size={16} className="mr-2" data-id="element-8505" />
            홈으로 돌아가기
          </Link>

          <header className="mb-12" data-id="element-8506">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight leading-tight" data-id="element-8507">
              {articleData.title}
            </h1>
            <div className="flex items-center text-gray-600 text-sm gap-3" data-id="element-8508">
              <time dateTime={articleData.publishDate} data-id="element-8509">
                {new Date(articleData.publishDate).toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              </time>
              <span className="text-gray-300" data-id="element-8510">•</span>
              <span data-id="element-8511">{articleData.author}</span>
            </div>
          </header>

          <div className="mb-12" data-id="element-8512">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sJh1uV1DahBAgXTJX4idSp/1763121685117.jpg" alt="AI가 수동적 공격적인 문자 메시지 분석" className="w-full rounded-2xl shadow-lg" data-id="element-8513" />
          </div>

          <div className="bg-white rounded-2xl p-7 border border-gray-200 mb-12" data-id="element-8514">
            <h2 className="font-bold text-lg mb-5" data-id="element-8515">목차</h2>
            <ol className="space-y-3 text-sm list-decimal list-inside" data-id="element-8516">
              <li data-id="element-8517">
                <a href="#sure-whatever" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-8518">
                  고전적인 "그래, 뭐든지"
                </a>
              </li>
              <li data-id="element-8519">
                <a href="#backhanded-compliment" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-8520">
                  비꼬는 칭찬
                </a>
              </li>
              <li data-id="element-8521">
                <a href="#friendly-reminder" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-8522">
                  사실은 공격인 "친절한 알림"
                </a>
              </li>
              <li data-id="element-8523">
                <a href="#ghostly-reply" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-8524">
                  유령 같은 답장
                </a>
              </li>
              <li data-id="element-8525">
                <a href="#sarcasm-humor" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-8526">
                  유머로 위장한 비꼬기
                </a>
              </li>
              <li data-id="element-8527">
                <a href="#conditional-niceness" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-8528">
                  조건부 친절
                </a>
              </li>
              <li data-id="element-8529">
                <a href="#rollercoaster" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-8530">
                  왔다 갔다 롤러코스터
                </a>
              </li>
            </ol>
          </div>

          <div className="prose prose-lg prose-gray max-w-none" data-id="element-8531">
            <p className="text-xl mb-8 leading-relaxed text-gray-700" data-id="element-8532">
              짜증나거나 좌절하거나 혼란스럽게 만들었음에도 불구하고 과민
              반응하는 것인지 의문이 들게 만드는 문자를 받은 적이 있나요? 그것이{' '}
              <strong data-id="element-8533">수동적 공격적인 텍스트</strong>입니다.
            </p>
            <p className="mb-12 text-lg leading-relaxed text-gray-600" data-id="element-8534">
              저희 <strong data-id="element-8535">AI 감지</strong>는 이러한{' '}
              <strong data-id="element-8536">조작적인 행동</strong> 패턴을 즉시 포착할 수 있습니다.
              여기 궁극적인 가이드가 있습니다.
            </p>

            <section id="sure-whatever" className="mb-12" data-id="element-8537">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-8538">
                1️⃣ 고전적인 "그래, 뭐든지"
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-8539">
                <p className="italic text-gray-700 text-lg" data-id="element-8540">
                  "그래, 네가 행복하다면 뭐든지..."
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-8541">
                언뜻 보기에는 동의하는 것처럼 보이지만 그 밑에는? 무시나
                비꼬기가 있습니다. AI는 이러한 문구를 표시합니다.
              </p>
            </section>

            <section id="backhanded-compliment" className="mb-12" data-id="element-8542">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-8543">
                2️⃣ 비꼬는 칭찬
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-8544">
                <p className="italic text-gray-700 text-lg" data-id="element-8545">
                  "와, 네가 실제로 그걸 해냈네... 잘됐네."
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-8546">
                좋게 들리나요? 사실 그렇지 않습니다. 이런 은밀한 공격은 자신감을
                깎아내립니다.
              </p>
            </section>

            <section id="friendly-reminder" className="mb-12" data-id="element-8547">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-8548">
                3️⃣ 사실은 공격인 "친절한 알림"
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-8549">
                <p className="italic text-gray-700 text-lg" data-id="element-8550">
                  "그냥 알려주는 건데... 내가 부엌 전체를 청소했어."
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-8551">
                수동적 공격적인 텍스트는 종종 비판을 유용한 정보로 위장합니다.
              </p>
            </section>

            <section id="ghostly-reply" className="mb-12" data-id="element-8552">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-8553">
                4️⃣ 유령 같은 답장
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-8554">
                <p className="italic text-gray-700 text-lg" data-id="element-8555">
                  "ㅇㅇ..." 또는 답장 없음
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-8556">
                짧거나 지연되거나 누락된 응답은 대면 없이 처벌할 수 있습니다.
              </p>
            </section>

            <section id="sarcasm-humor" className="mb-12" data-id="element-8557">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-8558">
                5️⃣ 유머로 위장한 비꼬기
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-8559">
                <p className="italic text-gray-700 text-lg" data-id="element-8560">
                  "오, 넌 정말 똑똑하구나 😏"
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-8561">
                은밀한 비꼬기는 핵심 도구입니다. 저희 AI는 맥락을 포착합니다.
              </p>
            </section>

            <section id="conditional-niceness" className="mb-12" data-id="element-8562">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-8563">
                6️⃣ 조건부 친절
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-8564">
                <p className="italic text-gray-700 text-lg" data-id="element-8565">
                  "도와줄게... 약속이 없다면."
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-8566">
                수동적 공격적인 사람은 죄책감을 유발하기 위해 경계를 설정합니다.
              </p>
            </section>

            <section id="rollercoaster" className="mb-12" data-id="element-8567">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-8568">
                7️⃣ 왔다 갔다 롤러코스터
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-8569">
                <p className="italic text-gray-700 text-lg" data-id="element-8570">
                  텍스트에서의 극단적인 고저
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-8571">
                변동하는 어조는 조작적인 행동의 위험 신호입니다.
              </p>
            </section>

            <section className="mb-12" data-id="element-8572">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-8573">
                🛡 자신을 보호하는 방법
              </h2>
              <ul className="space-y-4 text-lg text-gray-600" data-id="element-8574">
                <li className="flex items-start gap-3" data-id="element-8575">
                  <span className="text-gray-900 font-bold" data-id="element-8576">•</span>
                  <span data-id="element-8577">직감을 믿으세요.</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-8578">
                  <span className="text-gray-900 font-bold" data-id="element-8579">•</span>
                  <span data-id="element-8580">스크린샷을 보관하세요.</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-8581">
                  <span className="text-gray-900 font-bold" data-id="element-8582">•</span>
                  <span data-id="element-8583">개별 텍스트를 과도하게 분석하지 마세요.</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-8584">
                  <span className="text-gray-900 font-bold" data-id="element-8585">•</span>
                  <span data-id="element-8586">Toxic Detector를 사용하세요.</span>
                </li>
              </ul>
            </section>

            <section className="mb-16" data-id="element-8587">
              <h2 className="text-3xl font-bold mb-8 tracking-tight" data-id="element-8588">
                자주 묻는 질문
              </h2>
              <div className="space-y-6" data-id="element-8589">
                {faqSchema.mainEntity.map((faq, index) => <div key={index} className="bg-white rounded-xl p-6 border border-gray-200" data-id="element-8590">
                    <h3 className="font-bold text-lg mb-3 text-gray-900" data-id="element-8591">
                      {faq.name}
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-8592">
                      {faq.acceptedAnswer.text}
                    </p>
                  </div>)}
              </div>
            </section>
          </div>

          <div className="bg-gray-900 rounded-2xl p-12 text-center text-white mt-16" data-id="element-8593">
            <h2 className="text-3xl font-bold mb-4 tracking-tight" data-id="element-8594">
              💀 해독을 멈추세요. 알기 시작하세요.
            </h2>
            <p className="text-gray-300 mb-8 text-lg leading-relaxed max-w-2xl mx-auto" data-id="element-8595">
              추측은 그만하세요. 텍스트를 Toxic Detector에 업로드하고 즉시 독성
              점수를 받으세요.
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/20" data-id="element-8596">
              <p className="font-bold text-xl" data-id="element-8597">
                "🚩 65% 수동적 공격성 감지 — 반복되는 무시 행동 발견!"
              </p>
            </div>
            <Link to="/upload" className="inline-block bg-white text-gray-900 px-10 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors" data-id="element-8598">
              지금 채팅 스캔하기 →
            </Link>
          </div>

          <RelatedBlogPosts posts={[{
          title: '당신의 전 애인은 내현적 나르시시스트입니까?',
          description: '텍스트 분석을 통해 전 애인이 내현적 나르시시스트 행동의 징후를 보이는지 알아보세요.',
          slug: 'is-your-ex-a-covert-narcissist',
          emoji: '🎭'
        }, {
          title: '용어 배우기: 나르시시즘 & 가스라이팅',
          description: '관계에서의 나르시시즘과 가스라이팅 패턴을 이해하세요.',
          slug: 'narcissism-gaslighting-explained',
          emoji: '💀'
        }]} data-id="element-8599" />

          <div className="mt-12 text-center" data-id="element-8600">
            <button onClick={handleShare} className="inline-flex items-center text-gray-700 hover:text-gray-900 font-medium transition-colors" data-id="element-8601">
              <Share2 size={18} className="mr-2" data-id="element-8602" />이 기사 공유하기
            </button>
          </div>
        </article>

        <footer className="bg-gray-900 text-white py-12 mt-16" data-id="element-8603">
          <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-8604">
            <p className="text-gray-400 text-sm" data-id="element-8605">
              © {new Date().getFullYear()} ToxicDetector. 전권 소유.
            </p>
          </div>
        </footer>
      </div>
    </>;
}